import { isRejectedWithValue, Middleware } from "@reduxjs/toolkit";
import { toaster } from "../components/ui/toaster";

// Define types for RTK Query errors
interface RTKQueryError {
  status?: string | number;
  data?: {
    detail?: string;
    message?: string;
    [key: string]: any;
  };
  error?: string;
}

/**
 * Global error handling middleware for RTK Query
 * This will catch all API errors and show toast notifications
 */
export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  // RTK Query uses `isRejectedWithValue` to check for errors
  if (isRejectedWithValue(action)) {
    console.error("RTK Query Error:", action);

    const error = action.payload as RTKQueryError;
    let title = "API Error";
    let description = "An error occurred while processing your request.";

    // Handle different types of errors
    if (error?.status === "FETCH_ERROR") {
      title = "Network Error";
      description =
        "Unable to connect to the server. Please check your internet connection.";
    } else if (error?.status === "PARSING_ERROR") {
      title = "Data Error";
      description = "Error processing server response. Please try again.";
    } else if (error?.status === "TIMEOUT_ERROR") {
      title = "Timeout Error";
      description = "Request timed out. Please try again.";
    } else if (error?.status === 401) {
      title = "Authentication Error";
      description = "Please login again to continue.";
      // You might want to redirect to login page here
    } else if (error?.status === 403) {
      title = "Access Denied";
      description = "You do not have permission to perform this action.";
    } else if (error?.status === 404) {
      title = "Not Found";
      description = "The requested resource was not found.";
    } else if (error?.status === 500) {
      title = "Server Error";
      description = "Internal server error. Please try again later.";
    } else if (error?.data?.detail) {
      // Use server-provided error message
      description = error.data.detail;
    } else if (error?.data?.message) {
      description = error.data.message;
    }

    // Show toast notification
    toaster.create({
      title,
      description,
      type: "error",
      duration: 5000,
    });
  }

  return next(action);
};

/**
 * Custom error handler for specific components
 * Use this when you want to handle errors differently in specific components
 */
export const handleApiError = (error: any, customMessage?: string) => {
  console.error("API Error:", error);

  const message =
    customMessage ||
    error?.data?.detail ||
    error?.data?.message ||
    "An unexpected error occurred";

  toaster.create({
    title: "Error",
    description: message,
    type: "error",
    duration: 5000,
  });
};
